import { useCallback, useEffect, useRef, useState } from 'react';
import { TbSettingsCog } from 'react-icons/tb';
import { IoIosAddCircle } from 'react-icons/io';
import { FaRegTrashAlt } from 'react-icons/fa';
import { HiUserGroup } from 'react-icons/hi2';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import flatpickr from 'flatpickr';
import { Thai } from 'flatpickr/dist/l10n/th';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

import {
  deleteStudentsService,
  getEducationsByTeacherService,
  getStageDataAnalysisService,
  getStageDataStudentsService,
} from '../../../../services/externalv2_2';
import GroupsDialog from '../../../../components/Dialogs/Teachers/GroupsDialog';
import { useNavigate } from 'react-router-dom';
import tw from 'tailwind-styled-components';
import Checkbox from '../../../../components/Checkboxes/Checkbox';
import CheckboxFive from '../../../../components/Checkboxes/CheckboxFive';
import { useForm } from 'react-hook-form';

dayjs.extend(customParseFormat);

// configs
const bgColors: any = {
  st1_11: 'bg-[#506CFF]',
  st1_12: 'bg-[#FF8466]',
  st1_13: 'bg-[#506CFF]',
  st1_21: 'bg-[#FF8466]',
  st1_22: 'bg-[#506CFF]',
  st1_23: 'bg-[#FF8466]',
  st1_31: 'bg-[#506CFF]',
  st1_32: 'bg-[#FF8466]',
  st1_33: 'bg-[#506CFF]',
  st1_41: 'bg-[#FF8466]',
  st1_42: 'bg-[#506CFF]',
  st1_43: 'bg-[#FF8466]',
  st1_51: 'bg-[#506CFF]',
  st1_52: 'bg-[#FF8466]',
  st1_53: 'bg-[#506CFF]',
  st1_61: 'bg-[#FF8466]',
  st1_62: 'bg-[#506CFF]',
  st1_63: 'bg-[#FF8466]',
  st1_71: 'bg-[#506CFF]',
  st1_72: 'bg-[#FF8466]',
  st1_73: 'bg-[#506CFF]',
  st1_81: 'bg-[#FF8466]',
  st1_82: 'bg-[#506CFF]',
  st1_83: 'bg-[#FF8466]',
  st1_91: 'bg-[#506CFF]',
  // st1_92: 'bg-[#FF8466]',
  st1_93: 'bg-[#FF8466]',

  sp2_11: 'bg-[#506CFF]',
  sp2_12: 'bg-[#FF8466]',
  sp2_21: 'bg-[#506CFF]',
  sp2_22: 'bg-[#FF8466]',
  sp2_31: 'bg-[#506CFF]',
  sp2_32: 'bg-[#FF8466]',
  sp2_41: 'bg-[#506CFF]',
  sp2_42: 'bg-[#FF8466]',
  sp2_51: 'bg-[#2A969D]',
  sp2_52: 'bg-[#35B2BB]',
  sp2_53: 'bg-[#3DD5DF]',
  all: 'bg-[#A5F0F5]',
  room: 'bg-[#26C1CC]',

  st3_11: 'bg-[#506CFF]',
  st3_12: 'bg-[#FF8466]',
  st3_21: 'bg-[#506CFF]',
  st3_22: 'bg-[#FF8466]',
  st3_31: 'bg-[#506CFF]',
  st3_32: 'bg-[#FF8466]',
  st3_41: 'bg-[#506CFF]',
  st3_42: 'bg-[#FF8466]',
  st3_51: 'bg-[#506CFF]',
  st3_52: 'bg-[#FF8466]',
  st3_61: 'bg-[#506CFF]',
  st3_62: 'bg-[#FF8466]',

  st4_11: 'bg-[#506CFF]',
  st4_12: 'bg-[#FF8466]',
  st4_13: 'bg-[#506CFF]',
  st4_21: 'bg-[#FF8466]',
  st4_22: 'bg-[#506CFF]',
  st4_23: 'bg-[#FF8466]',
  st4_31: 'bg-[#506CFF]',
  st4_32: 'bg-[#FF8466]',
  st4_33: 'bg-[#506CFF]',
  st4_41: 'bg-[#FF8466]',
  st4_42: 'bg-[#506CFF]',
  st4_43: 'bg-[#FF8466]',
};

const brColors: any = {
  st1_11: 'border-[#506CFF]',
  st1_12: 'border-[#FF8466]',
  st1_13: 'border-[#506CFF]',
  st1_21: 'border-[#FF8466]',
  st1_22: 'border-[#506CFF]',
  st1_23: 'border-[#FF8466]',
  st1_31: 'border-[#506CFF]',
  st1_32: 'border-[#FF8466]',
  st1_33: 'border-[#506CFF]',
  st1_41: 'border-[#FF8466]',
  st1_42: 'border-[#506CFF]',
  st1_43: 'border-[#FF8466]',
  st1_51: 'border-[#506CFF]',
  st1_52: 'border-[#FF8466]',
  st1_53: 'border-[#506CFF]',
  st1_61: 'border-[#FF8466]',
  st1_62: 'border-[#506CFF]',
  st1_63: 'border-[#FF8466]',
  st1_71: 'border-[#506CFF]',
  st1_72: 'border-[#FF8466]',
  st1_73: 'border-[#506CFF]',
  st1_81: 'border-[#FF8466]',
  st1_82: 'border-[#506CFF]',
  st1_83: 'border-[#FF8466]',
  st1_91: 'border-[#506CFF]',
  // st1_92: 'border-[#FF8466]',
  st1_93: 'border-[#FF8466]',

  sp2_11: 'border-[#506CFF]',
  sp2_12: 'border-[#FF8466]',
  sp2_21: 'border-[#506CFF]',
  sp2_22: 'border-[#FF8466]',
  sp2_31: 'border-[#506CFF]',
  sp2_32: 'border-[#FF8466]',
  sp2_41: 'border-[#506CFF]',
  sp2_42: 'border-[#FF8466]',
  sp2_51: 'border-[#2A969D]',
  sp2_52: 'border-[#35B2BB]',
  sp2_53: 'border-[#3DD5DF]',

  st3_11: 'border-[#506CFF]',
  st3_12: 'border-[#FF8466]',
  st3_21: 'border-[#506CFF]',
  st3_22: 'border-[#FF8466]',
  st3_31: 'border-[#506CFF]',
  st3_32: 'border-[#FF8466]',
  st3_41: 'border-[#506CFF]',
  st3_42: 'border-[#FF8466]',
  st3_51: 'border-[#506CFF]',
  st3_52: 'border-[#FF8466]',
  st3_61: 'border-[#506CFF]',
  st3_62: 'border-[#FF8466]',

  st4_11: 'border-[#506CFF]',
  st4_12: 'border-[#FF8466]',
  st4_13: 'border-[#506CFF]',
  st4_21: 'border-[#FF8466]',
  st4_22: 'border-[#506CFF]',
  st4_23: 'border-[#FF8466]',
  st4_31: 'border-[#506CFF]',
  st4_32: 'border-[#FF8466]',
  st4_33: 'border-[#506CFF]',
  st4_41: 'border-[#FF8466]',
  st4_42: 'border-[#506CFF]',
  st4_43: 'border-[#FF8466]',
};

const PHASE_SCORE_CONFIG: Record<
  number,
  { groups: Array<{ label: string; colSpan: number }>; subHeaders: string[] }
> = {
  1: {
    groups: [{ label: 'คะแนนเกม', colSpan: 26 }],
    subHeaders: [
      '1.1',
      '1.2',
      '1.3',
      '2.1',
      '2.2',
      '2.3',
      '3.1',
      '3.2',
      '3.3',
      '4.1',
      '4.2',
      '4.3',
      '5.1',
      '5.2',
      '5.3',
      '6.1',
      '6.2',
      '6.3',
      '7.1',
      '7.2',
      '7.3',
      '8.1',
      '8.2',
      '8.3',
      '9.1',
      // '9.2',
      '9.3',
    ],
  },
  2: {
    groups: [
      { label: 'คะแนนเกมสระ', colSpan: 8 },
      { label: 'คะแนนเกมวรรณยุกต์', colSpan: 3 },
    ],
    subHeaders: [
      '1.1',
      '1.2',
      '2.1',
      '2.2',
      '3.1',
      '3.2',
      '4.1',
      '4.2',
      '1',
      '2',
      '3',
    ],
  },
  3: {
    groups: [{ label: 'คะแนนเกม', colSpan: 12 }],
    subHeaders: [
      '1.1',
      '1.2',
      '2.1',
      '2.2',
      '3.1',
      '3.2',
      '4.1',
      '4.2',
      '5.1',
      '5.2',
      '6.1',
      '6.2',
    ],
  },
  4: {
    groups: [{ label: 'คะแนนเกม', colSpan: 12 }],
    subHeaders: [
      '1.1',
      '1.2',
      '1.3',
      '2.1',
      '2.2',
      '2.3',
      '3.1',
      '3.2',
      '3.3',
      '4.1',
      '4.2',
      '4.3',
    ],
  },
};

const DataScope = (props: any) => {
  return (
    <div className="flex flex-col items-center gap">
      <div
        className={`py-[2px] w-20 border-2 ${
          props.type === 'room' ? 'border-[#506CFF]' : 'border-[#A5F0F5]'
        } rounded-full text-center`}
      >
        {props.value || '-'}
      </div>
    </div>
  );
};

const DashboardTeacher: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const teacherid = searchParams.get('teacherid');

  const [groupDialogOpen, setGroupDialogOpen] = useState<boolean>(false);
  const [isCheckAll, setIsCheckAll] = useState<boolean>(false);
  const [studentRemoveList, setStudentRemoveList] = useState<string[]>([]);
  const [stageDataAnalysis, setStageDataAnalysis] = useState<any>({
    playerCount: 0,
    sscores: [],
  });
  const [stageDataStudents, setStageDataStudents] = useState<any>({
    groups: [],
  });

  const [educations, setEducations] = useState<any>({
    educations: [],
  });

  const [colspan, setColspan] = useState<number>(0);
  const [eduRegs, setEduRegs] = useState<any[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const dateFromRef = useRef<HTMLInputElement>(null);
  const dateToRef = useRef<HTMLInputElement>(null);
  const dateFromPickerRef = useRef<FlatpickrInstance | null>(null);
  const dateToPickerRef = useRef<FlatpickrInstance | null>(null);

  const handleGroupDialogOpen = () => setGroupDialogOpen(true);
  const handleGroupDialogClose = () => setGroupDialogOpen(false);

  const { getValues, setValue, watch } = useForm<{
    educationid: string | null;
  }>({
    defaultValues: {
      educationid: null,
    },
  });

  const watchEducationid = watch('educationid');

  const getDateFilter = useCallback(() => {
    const dateFilter: { gte?: string; lte?: string } = {};
    if (dateFrom) {
      dateFilter.gte = dayjs(dateFrom, 'DD-MM-YYYY')
        .startOf('day')
        .toISOString();
    }
    if (dateTo) {
      dateFilter.lte = dayjs(dateTo, 'DD-MM-YYYY').endOf('day').toISOString();
    }
    return dateFilter;
  }, [dateFrom, dateTo]);

  const handleEducationCheckboxChange = (
    e: React.FormEvent<HTMLInputElement>,
  ) => {
    const eduid = Number(e.currentTarget.value);
    const dataEduRegs = [...eduRegs];

    if (eduid === -1) {
      setEduRegs([]);
      const firstEducationId = educations.educations[0]?.educationid;
      if (firstEducationId != null) {
        setValue('educationid', String(firstEducationId));
      }
      return;
    }

    const zeroIndex = dataEduRegs.indexOf(0);
    if (zeroIndex !== -1) dataEduRegs.splice(zeroIndex, 1);

    const indexToRemove = dataEduRegs.findIndex((el) => el === eduid);
    if (indexToRemove !== -1) {
      dataEduRegs.splice(indexToRemove, 1);
    } else {
      dataEduRegs.push(eduid);
    }

    setEduRegs([...dataEduRegs]);

    const selectedEducationId =
      dataEduRegs[0] ?? educations.educations[0]?.educationid;
    if (selectedEducationId != null) {
      setValue('educationid', String(selectedEducationId));
    }
  };

  useEffect(() => {
    if (!dateFromRef.current || !dateToRef.current) return;

    const sharedOptions = {
      locale: Thai,
      mode: 'single' as const,
      dateFormat: 'd-m-Y',
      altInput: true,
      altFormat: 'j F Y',
      allowInput: false,
      prevArrow:
        '<svg className="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M5.4 10.8l1.4-1.4-4-4 4-4L5.4 0 0 5.4z" /></svg>',
      nextArrow:
        '<svg className="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M1.4 10.8L0 9.4l4-4-4-4L1.4 0l5.4 5.4z" /></svg>',
    };

    dateFromPickerRef.current = flatpickr(dateFromRef.current, {
      ...sharedOptions,
      altInputClass: dateFromRef.current.className,
      onChange: (selectedDates) => {
        const value = selectedDates[0]
          ? dayjs(selectedDates[0]).format('DD-MM-YYYY')
          : '';
        setDateFrom(value);
        if (dateToPickerRef.current) {
          dateToPickerRef.current.set('minDate', selectedDates[0] || undefined);
        }
      },
    });

    dateToPickerRef.current = flatpickr(dateToRef.current, {
      ...sharedOptions,
      altInputClass: dateToRef.current.className,
      onChange: (selectedDates) => {
        const value = selectedDates[0]
          ? dayjs(selectedDates[0]).format('DD-MM-YYYY')
          : '';
        setDateTo(value);
        if (dateFromPickerRef.current) {
          dateFromPickerRef.current.set(
            'maxDate',
            selectedDates[0] || undefined,
          );
        }
      },
    });

    return () => {
      dateFromPickerRef.current?.destroy();
      dateToPickerRef.current?.destroy();
      dateFromPickerRef.current = null;
      dateToPickerRef.current = null;
    };
  }, []);

  const handleConfirmDeleteStudents = () => {
    withReactContent(Swal)
      .fire({
        title: 'คุณแน่ใจไหม?',
        text: 'ว่าต้องการลบข้อมูลนักเรียนที่เลือก',
        icon: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#d33',
        confirmButtonText: 'ใช่ฉันแน่ใจ',
        cancelButtonText: 'ยกเลิก',
        customClass: {
          confirmButton: 'bg-primary',
          popup: 'bg-white shadow-md rounded-lg p-4',
        },
      })
      .then((result) => {
        if (result.isConfirmed) {
          deleteStudentsService({ studentids: studentRemoveList }).then((_) => {
            setStudentRemoveList([]);
            Swal.fire({
              title: 'ลบแล้ว!',
              text: 'ข้อมูลนักเรียนของคุณถูกลบแล้ว',
              icon: 'success',
              confirmButtonText: 'ตกลง',
            }).then((_) => getStageDataStudents());
          });
        }
      });
  };

  const handleCheckBoxChanged = (e: React.FormEvent<HTMLInputElement>) => {
    let students = [...studentRemoveList];
    const value = e.currentTarget.value;
    if (value === 'all') {
      if (!isCheckAll) {
        for (const group of stageDataStudents.groups) {
          for (const student of group.students) {
            students.push(student._id);
          }
        }
      } else students = [];
      setIsCheckAll(!isCheckAll);
    } else {
      if (students.includes(value))
        students = students.filter((s) => s !== value);
      else students.push(value);
      setIsCheckAll(false);
    }
    setStudentRemoveList(students);
  };

  const getStageDataAnalysis = (educationId?: string | null) => {
    const educationid = educationId ?? getValues().educationid;
    const params = {
      ...(educationid ? { educationid } : {}),
      ...(teacherid ? { teacherid } : {}),
      ...getDateFilter(),
    };

    if (educationid != null)
      getStageDataAnalysisService(params).then((data) => {
        setStageDataAnalysis(data.results);
      });
  };

  const getStageDataStudents = (educationId?: string | null) => {
    const educationid = educationId ?? getValues().educationid;
    const params = {
      ...(educationid ? { educationid } : {}),
      ...(teacherid ? { teacherid } : {}),
      ...getDateFilter(),
    };

    if (educationid != null)
      getStageDataStudentsService(params).then((data) => {
        const phaseConfig = PHASE_SCORE_CONFIG[data.results.phase] || {
          groups: [],
          subHeaders: [],
        };
        setColspan(phaseConfig.subHeaders.length);
        setStageDataStudents(data.results);
      });
  };

  const getEducationsByTeacher = () => {
    const params = {
      ...(teacherid ? { teacherid } : {}),
    };

    getEducationsByTeacherService(params).then((data) => {
      const firstEducationId = data.results.educations[0]?.educationid;
      const educationid =
        firstEducationId != null ? String(firstEducationId) : null;

      setEducations(data.results);
      if (firstEducationId != null) {
        setEduRegs([Number(firstEducationId)]);
      }
      setValue('educationid', educationid);
    });
  };

  useEffect(getEducationsByTeacher, []);

  useEffect(() => {
    if (!watchEducationid) return;
    getStageDataAnalysis(watchEducationid);
  }, [watchEducationid, getDateFilter]);

  useEffect(() => {
    getStageDataStudents(watchEducationid);
  }, [watchEducationid]);

  const phase = stageDataStudents?.phase;
  const scoreConfig = PHASE_SCORE_CONFIG[phase] || {
    groups: [],
    subHeaders: [],
  };
  const scoreHeaderGroups = scoreConfig.groups;
  const scoreSubHeaders = scoreConfig.subHeaders;

  return (
    <div className="flex flex-col gap-8 rounded-xl border border-stroke bg-white shadow-default p-8">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div className="flex flex-col gap-2 self-center">
            <h1 className="text-black text-2xl dark:text-white font-bold">
              ข้อมูลประกอบการวิเคราะห์
            </h1>
            <p className="text-sm text-meta-7">
              หมายเหตุ: (ส่วนที่แสดงค่า mean median mode)
              ค่าคะแนนในส่วนนี้ได้มาจากการเล่นในครั้งแรกของเด็ก
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-between gap-4">
            <div className="flex flex-col border-[5px] border-[#FFC700] gap-2 text-center self-end">
              <div className="bg-[#FFC700] px-12 text-white font-bold">
                <p>จำนวนนักเรียน</p>
                <p>ทั้งประเทศ</p>
              </div>

              <div className="text-black font-bold p-2">
                {stageDataAnalysis.playerCount.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <h6 className="font-bold text-xl">
            การฟิลเตอร์ข้อมูล (ระดับการศึกษา)
          </h6>
          <div className="flex flex-wrap gap-4 text-lg">
            {/* <CheckboxFive
              label="ทุกคน"
              id={-1}
              isChecked={!eduRegs.length}
              onChange={handleEducationCheckboxChange}
            /> */}
            {educations.educations.map((item: any) => (
              <CheckboxFive
                key={item.educationid}
                label={item.name_th}
                id={item.educationid}
                isChecked={eduRegs.includes(item.educationid)}
                onChange={handleEducationCheckboxChange}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h6 className="font-bold text-xl">การฟิลเตอร์ข้อมูล (ช่วงวันที่)</h6>
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end">
            <div className="flex flex-col gap-1 xl:w-1/4">
              <label className="mb-2.5 block font-medium text-black dark:text-white">
                ตั้งแต่วันที่
              </label>
              <div className="relative">
                <Input
                  ref={dateFromRef}
                  className="form-datepicker cursor-pointer"
                  placeholder="วัน เดือน, ปี"
                  data-class="flatpickr-right"
                  readOnly
                />
                <div className="pointer-events-none absolute inset-0 left-auto right-5 flex items-center">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.7504 2.9812H14.2879V2.36245C14.2879 2.02495 14.0066 1.71558 13.641 1.71558C13.2754 1.71558 12.9941 1.99683 12.9941 2.36245V2.9812H4.97852V2.36245C4.97852 2.02495 4.69727 1.71558 4.33164 1.71558C3.96602 1.71558 3.68477 1.99683 3.68477 2.36245V2.9812H2.25039C1.29414 2.9812 0.478516 3.7687 0.478516 4.75308V14.5406C0.478516 15.4968 1.26602 16.3125 2.25039 16.3125H15.7504C16.7066 16.3125 17.5223 15.525 17.5223 14.5406V4.72495C17.5223 3.7687 16.7066 2.9812 15.7504 2.9812ZM1.77227 8.21245H4.16289V10.9968H1.77227V8.21245ZM5.42852 8.21245H8.38164V10.9968H5.42852V8.21245ZM8.38164 12.2625V15.0187H5.42852V12.2625H8.38164V12.2625ZM9.64727 12.2625H12.6004V15.0187H9.64727V12.2625ZM9.64727 10.9968V8.21245H12.6004V10.9968H9.64727ZM13.8379 8.21245H16.2285V10.9968H13.8379V8.21245ZM2.25039 4.24683H3.71289V4.83745C3.71289 5.17495 3.99414 5.48433 4.35977 5.48433C4.72539 5.48433 5.00664 5.20308 5.00664 4.83745V4.24683H13.0504V4.83745C13.0504 5.17495 13.3316 5.48433 13.6973 5.48433C14.0629 5.48433 14.3441 5.20308 14.3441 4.83745V4.24683H15.7504C16.0316 4.24683 16.2566 4.47183 16.2566 4.75308V6.94683H1.77227V4.75308C1.77227 4.47183 1.96914 4.24683 2.25039 4.24683ZM1.77227 14.5125V12.2343H4.16289V14.9906H2.25039C1.96914 15.0187 1.77227 14.7937 1.77227 14.5125ZM15.7504 15.0187H13.8379V12.2625H16.2285V14.5406C16.2566 14.7937 16.0316 15.0187 15.7504 15.0187Z"
                      fill="#64748B"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1 xl:w-1/4">
              <label className="mb-2.5 block font-medium text-black dark:text-white">
                ถึงวันที่
              </label>
              <div className="relative">
                <Input
                  ref={dateToRef}
                  className="form-datepicker cursor-pointer"
                  placeholder="วัน เดือน, ปี"
                  data-class="flatpickr-right"
                  readOnly
                />
                <div className="pointer-events-none absolute inset-0 left-auto right-5 flex items-center">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.7504 2.9812H14.2879V2.36245C14.2879 2.02495 14.0066 1.71558 13.641 1.71558C13.2754 1.71558 12.9941 1.99683 12.9941 2.36245V2.9812H4.97852V2.36245C4.97852 2.02495 4.69727 1.71558 4.33164 1.71558C3.96602 1.71558 3.68477 1.99683 3.68477 2.36245V2.9812H2.25039C1.29414 2.9812 0.478516 3.7687 0.478516 4.75308V14.5406C0.478516 15.4968 1.26602 16.3125 2.25039 16.3125H15.7504C16.7066 16.3125 17.5223 15.525 17.5223 14.5406V4.72495C17.5223 3.7687 16.7066 2.9812 15.7504 2.9812ZM1.77227 8.21245H4.16289V10.9968H1.77227V8.21245ZM5.42852 8.21245H8.38164V10.9968H5.42852V8.21245ZM8.38164 12.2625V15.0187H5.42852V12.2625H8.38164V12.2625ZM9.64727 12.2625H12.6004V15.0187H9.64727V12.2625ZM9.64727 10.9968V8.21245H12.6004V10.9968H9.64727ZM13.8379 8.21245H16.2285V10.9968H13.8379V8.21245ZM2.25039 4.24683H3.71289V4.83745C3.71289 5.17495 3.99414 5.48433 4.35977 5.48433C4.72539 5.48433 5.00664 5.20308 5.00664 4.83745V4.24683H13.0504V4.83745C13.0504 5.17495 13.3316 5.48433 13.6973 5.48433C14.0629 5.48433 14.3441 5.20308 14.3441 4.83745V4.24683H15.7504C16.0316 4.24683 16.2566 4.47183 16.2566 4.75308V6.94683H1.77227V4.75308C1.77227 4.47183 1.96914 4.24683 2.25039 4.24683ZM1.77227 14.5125V12.2343H4.16289V14.9906H2.25039C1.96914 15.0187 1.77227 14.7937 1.77227 14.5125ZM15.7504 15.0187H13.8379V12.2625H16.2285V14.5406C16.2566 14.7937 16.0316 15.0187 15.7504 15.0187Z"
                      fill="#64748B"
                    />
                  </svg>
                </div>
              </div>
            </div>
            {(dateFrom || dateTo) && (
              <button
                type="button"
                className="rounded-full border border-[#D9D9D9] bg-[#D9D9D9] px-4 py-2 hover:bg-[#FFFFFF]"
                onClick={() => {
                  dateFromPickerRef.current?.clear();
                  dateToPickerRef.current?.clear();
                  dateFromPickerRef.current?.set('maxDate', undefined);
                  dateToPickerRef.current?.set('minDate', undefined);
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                ล้างวันที่
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 justify-center">
          {stageDataAnalysis.sscores.map((data: any, index: number) => {
            const bgClass = bgColors[data._id];
            const boderClass = brColors[data._id];

            return (
              <div
                key={index}
                className={`flex flex-col border-[5px] ${boderClass} gap-2`}
              >
                <div
                  className={`${bgClass} py-2 flex flex-col gap-2 border-none`}
                >
                  <div className="flex justify-between">
                    <p className="text-white text-lg font-semibold">
                      {data.stageName}
                    </p>

                    <p className="text-white text-right font-semibold self-center">
                      คะแนนเต็ม: {data.max}
                    </p>
                  </div>

                  <p className="text-white text-right font-semibold">
                    นักเรียนในห้อง: {data.playerRoomCount} คน
                  </p>

                  <p className="text-white text-right font-semibold">
                    ผู้เล่นทั้งหมด: {data.playerCount} คน
                  </p>
                </div>

                <div className="flex flex-col gap-2 p-2">
                  <div className="flex justify-between">
                    <p className="text-black font-semibold w-3/12 self-end">
                      Mean
                    </p>
                    <div className="flex justify-between gap-2">
                      <div>
                        <p className="text-black font-semibold text-center">
                          ประเทศ
                        </p>
                        <DataScope type="all" value={data.meanAll} />
                      </div>
                      <div>
                        <p className="text-black font-semibold text-center">
                          ห้อง
                        </p>
                        <DataScope type="room" value={data.mean} />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <p className="text-black font-semibold w-3/12 self-end">
                      Median
                    </p>
                    <div className="flex justify-between gap-2">
                      <div>
                        <DataScope type="all" value={data.medianAll} />
                      </div>
                      <div>
                        <DataScope type="room" value={data.median} />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <p className="text-black font-semibold w-3/12 self-end">
                      Mode
                    </p>
                    <div className="flex justify-between gap-2">
                      <div>
                        <DataScope type="all" value={data.modeAll} />
                      </div>
                      <div>
                        <DataScope type="room" value={data.mode} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="text-black text-2xl dark:text-white font-bold">
            ข้อมูลนักเรียนรายบุคคล
          </h1>
          <p className="text-sm text-meta-7">
            หมายเหตุ:
            ค่าคะแนนที่แสดงเป็นค่าคะแนนในครั้งที่เด็กเล่นได้คะแนนสูงสุด
          </p>
        </div>

        <div className="flex gap-4 px-4">
          <button
            onClick={() =>
              navigate('/sornthai/dashboard-teacher/create-student')
            }
            className="flex rounded-full border border-[#D9D9D9] bg-[#D9D9D9] p-2 hover:bg-[#FFFFFF]"
          >
            <IoIosAddCircle fontSize={24} className="text-green-600" />
            <p className="text-sm self-center ml-2 hidden sm:flex">
              เพิ่มนักเรียน
            </p>
          </button>
          <button
            onClick={handleGroupDialogOpen}
            className="flex rounded-full border border-[#D9D9D9] bg-[#D9D9D9] p-2 hover:bg-[#FFFFFF]"
          >
            <HiUserGroup fontSize={24} />
            <p className="text-sm self-center ml-2 hidden sm:flex">
              แก้ไขข้อมูลกลุ่ม
            </p>
          </button>
          <button
            onClick={handleConfirmDeleteStudents}
            className="flex rounded-full border border-[#D9D9D9] bg-[#D9D9D9] p-2 hover:bg-[#FFFFFF]"
            disabled={!studentRemoveList.length}
          >
            <FaRegTrashAlt fontSize={24} className="text-red-600" />
            <p className="text-sm self-center ml-2 hidden sm:flex">
              ลบนักเรียน
            </p>
          </button>
        </div>

        <div className="container mx-auto p-4 overflow-auto">
          <table className="min-w-full border-collapse border border-black table-auto overflow-scroll">
            <thead className="bg-[#808080] text-white font-normal">
              <tr>
                <Th rowSpan={2}>
                  <Checkbox
                    id="all"
                    onChange={handleCheckBoxChanged}
                    isChecked={isCheckAll}
                  />
                </Th>
                <Th rowSpan={2}>ชื่อจริง</Th>
                <Th rowSpan={2}>นามสกุล</Th>
                <Th rowSpan={2}>ชื่อเล่น</Th>
                {scoreHeaderGroups.map((header) => (
                  <Th key={header.label} colSpan={header.colSpan}>
                    {header.label}
                  </Th>
                ))}
                <Th rowSpan={2}></Th>
              </tr>
              <tr>
                {scoreSubHeaders.map((header) => (
                  <Th key={header}>{header}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stageDataStudents.groups.map((data: any, p: number) => (
                <>
                  <tr className="bg-gray-100" key={p}>
                    <td colSpan={15} className="py-2 px-4 font-semibold">
                      <div className="ml-12 flex gap-4">
                        {data._id && (
                          <button className="border rounded-full p-1">
                            <TbSettingsCog className="self-center" />
                          </button>
                        )}
                        <label className="text-black">
                          {data._id
                            ? `----- ${data?.group[0]?.group_name} -----`
                            : `----- ยังไม่ได้รับการจัดกลุ่ม -----`}
                        </label>
                      </div>
                    </td>
                  </tr>

                  {data.students.map((student: any, s: number) => (
                    <tr className="text-center" key={s}>
                      <Td>
                        <Checkbox
                          id={student._id}
                          onChange={handleCheckBoxChanged}
                          isChecked={studentRemoveList.includes(student._id)}
                        />
                      </Td>
                      <Td>{student.name}</Td>
                      <Td>{student.surname}</Td>
                      <Td>{student.nick || '-'}</Td>
                      {student.sscores.map((sscore: any, t: number) => (
                        <Td key={`score-${t}`}>{sscore.score}</Td>
                      ))}
                      {colspan - student.sscores.length !== 0 && (
                        <Td
                          colSpan={colspan - student.sscores.length}
                          key={`empty`}
                        >
                          ยังไม่มีคะแนน
                        </Td>
                      )}

                      <Td className="text-center">
                        <button
                          type="button"
                          className="border rounded-full p-1"
                          onClick={() =>
                            navigate(
                              `/sornthai/dashboard-teacher/user-data-stages/${student._id}`,
                            )
                          }
                        >
                          <TbSettingsCog className="self-center" />
                        </button>
                      </Td>
                    </tr>
                  ))}
                </>
              ))}
              {!stageDataStudents.groups.length && (
                <tr className="text-center">
                  <Td colSpan={16}>ไม่พบข้อมูล</Td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* dialog */}
      {groupDialogOpen && <GroupsDialog onClose={handleGroupDialogClose} />}
    </div>
  );
};

const Th = tw.th`py-2 px-4 border border-black font-normal`;
const Td = tw.td`py-2 px-4 border`;
const Input = tw.input`w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark`;

export default DashboardTeacher;
